import { PRO_PROMO_FALL26 } from '@/lib/constants';
import type { SubscriptionInterval } from '@/services/subscription-interval.service';
import type { MembershipTier } from '@/types/live-session';

/**
 * Pure audience rules for the Fall '26 OS Pro promo banner. Mirrors the WP
 * gate in client-mu-plugins/osj-pro-promo-banner-fall26.php:
 *   - Lifetime owners        → never
 *   - OS Regular members     → yes, any billing interval
 *   - OS Pro monthly members → yes
 *   - OS Pro annual / other  → no
 *   - non-members            → no
 */
export function isProPromoAudience(input: {
  tier: MembershipTier;
  interval: SubscriptionInterval;
  hasLifetime: boolean;
}): boolean {
  if (input.hasLifetime) return false;
  if (input.tier === 'regular') return true;
  if (input.tier === 'pro') return input.interval === 'month';
  return false;
}

export function isProPromoWindow(now: number): boolean {
  return now >= PRO_PROMO_FALL26.START_MS && now <= PRO_PROMO_FALL26.END_MS;
}

/** Final-days phase: show the countdown clock instead of the "Limited Time" pill. */
export function isProPromoCountdownPhase(now: number): boolean {
  return now >= PRO_PROMO_FALL26.COUNTDOWN_SHOW_FROM_MS;
}

/** iOS window: tracks the App Store sale dates, not the web promo. */
export function isNativeProPromoWindow(now: number): boolean {
  return now >= PRO_PROMO_FALL26.NATIVE_START_MS && now <= PRO_PROMO_FALL26.END_MS;
}

/**
 * Native (iOS) audience — Dan, 2026-09-28: promote the App Store Pro annual
 * sale to existing members who aren't on trial, excluding OS Pro members;
 * include individual course purchasers and OS-plan (Regular) members. Free
 * accounts with no purchases and non-members get nothing.
 */
export function isNativeProPromoAudience(input: {
  tier: MembershipTier;
  onTrial: boolean;
  hasPurchasedCourses: boolean;
}): boolean {
  if (input.onTrial) return false;
  if (input.tier === 'pro') return false;
  if (input.tier === 'regular') return true;
  return input.hasPurchasedCourses;
}

/** Reviewer accounts that see the banner immediately, bypassing window + audience. */
export function isProPromoPreviewUser(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return (PRO_PROMO_FALL26.PREVIEW_EMAILS as readonly string[]).includes(normalized);
}
